import { useEffect, useRef } from 'react';
import { Checkbox } from '@/components/shadcn/checkbox.tsx';
import { Label } from '@/components/shadcn/label.tsx';
import {
  govtIntakeFormProgramTypeOptions,
  govtIntakeFormTiedToOptions,
  govtIntakeFormTrackingMethodOptions,
} from '@/data/govtIntakeStaticData.ts';
import type { ValidationChange } from '@/modules/governmentIntake/base-entity.tsx';
import HeaderAndSub from '@/modules/governmentIntake/components/shared-component.tsx';
import {
  EMPTY_PROGRAM_STRUCTURE,
  type ProgramStructureData,
  useIntakeForm,
} from '@/modules/governmentIntake/context/intake-form-context.tsx';

export default function ProgramSubStructure({ onValidationChange }: ValidationChange) {
  const { data, updateProgramSubSection } = useIntakeForm();
  const structureData = data.programs?.structure ?? EMPTY_PROGRAM_STRUCTURE;

  const onValidationChangeRef = useRef(onValidationChange);
  onValidationChangeRef.current = onValidationChange;

  useEffect(() => {
    onValidationChangeRef.current?.(true);
  }, []);

  const updateStructure = (newData: ProgramStructureData) => {
    updateProgramSubSection('structure', newData);
  };

  const handleProgramTypeChange = (id: string, checked: boolean) => {
    updateStructure({
      ...structureData,
      programTypes: { ...structureData.programTypes, [id]: checked },
    });
  };

  const handleTrackingChange = (id: string, checked: boolean) => {
    updateStructure({
      ...structureData,
      programTracking: { ...structureData.programTracking, [id]: checked },
    });
  };

  const handleDependencyChange = (id: string, checked: boolean) => {
    updateStructure({
      ...structureData,
      programTiedTo: { ...structureData.programTiedTo, [id]: checked },
    });
  };

  return (
    <div className='flex w-full flex-col overflow-y-auto'>
      <div className='w-full max-w-5xl'>
        <header className='shrink-0 pt-4'>
          <HeaderAndSub title='Program Structure (High-Level)' subTitle='Quick overview of program types and tracking methods.' />
        </header>

        <section className='flex-1 overflow-y-auto pb-4'>
          <div className='space-y-6 pt-8'>
            <div className='h-px bg-gray-200 my-4' />

            {/* Program Types */}
            <div className='space-y-3'>
              <Label className='block text-sm font-semibold text-gray-900 mb-2'>Types of programs you run</Label>
              {govtIntakeFormProgramTypeOptions.map((program) => (
                <div key={`type-${program.id}`} className='flex items-center space-x-3'>
                  <Checkbox
                    id={`type-${program.id}`}
                    checked={Boolean(structureData.programTypes[program.id])}
                    onCheckedChange={(checked) => handleProgramTypeChange(program.id, Boolean(checked))}
                  />
                  <Label htmlFor={`type-${program.id}`} className='cursor-pointer select-none text-sm'>
                    {program.label}
                  </Label>
                </div>
              ))}
            </div>

            {/* Tracking Methods */}
            <div className='space-y-3'>
              <Label className='block text-sm font-semibold text-gray-900 mb-2'>How programs are tracked today</Label>
              {govtIntakeFormTrackingMethodOptions.map((tracking) => (
                <div key={`tracking-${tracking.id}`} className='flex items-center space-x-3'>
                  <Checkbox
                    id={`tracking-${tracking.id}`}
                    checked={Boolean(structureData.programTracking[tracking.id])}
                    onCheckedChange={(checked) => handleTrackingChange(tracking.id, Boolean(checked))}
                  />
                  <Label htmlFor={`tracking-${tracking.id}`} className='cursor-pointer select-none text-sm'>
                    {tracking.label}
                  </Label>
                </div>
              ))}
            </div>

            {/* Program Dependencies */}
            <div className='space-y-3'>
              <Label className='block text-sm font-semibold text-gray-900 mb-2'>Are programs tied to:</Label>
              {govtIntakeFormTiedToOptions.map((option) => (
                <div key={`dependency-${option.id}`} className='flex items-center space-x-3'>
                  <Checkbox
                    id={`dependency-${option.id}`}
                    checked={Boolean(structureData.programTiedTo[option.id])}
                    onCheckedChange={(checked) => handleDependencyChange(option.id, Boolean(checked))}
                  />
                  <Label htmlFor={`dependency-${option.id}`} className='cursor-pointer select-none text-sm'>
                    {option.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
